import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { Select } from "@dashboard/components/Select";
import { OrderErrorFragment } from "@dashboard/graphql";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { Box, Text, Textarea } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { cancelOrderDialogMessages } from "./messages";
import {
  buildOrderCancelFormData,
  OrderCancelDialogFormData,
  orderCancelReasons,
  OTHER_ORDER_CANCEL_REASON_CODE,
} from "./reasons";

export interface OrderCancelDialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  number: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: OrderCancelDialogFormData) => void;
  variant?: "default" | "after-refund" | "after-pending-refund";
}

export const OrderCancelDialog = (props: OrderCancelDialogProps) => {
  const {
    confirmButtonState,
    errors: apiErrors,
    number: orderNumber,
    open,
    onSubmit,
    onClose,
    variant = "default",
  } = props;
  const intl = useIntl();
  const errors = useModalDialogErrors(apiErrors, open);
  const [reasonCode, setReasonCode] = React.useState<string | null>(null);
  const [otherReason, setOtherReason] = React.useState("");

  // A reason belongs to one cancellation: start blank every time the dialog opens.
  React.useEffect(() => {
    if (!open) {
      setReasonCode(null);
      setOtherReason("");
    }
  }, [open]);

  const reasonOptions = orderCancelReasons.map(reason => ({
    value: reason.code,
    label: intl.formatMessage(reason.message),
  }));
  const isOtherReason = reasonCode === OTHER_ORDER_CANCEL_REASON_CODE;
  const formData = buildOrderCancelFormData(reasonCode, otherReason);

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header data-test-id="dialog-title">
          <FormattedMessage {...cancelOrderDialogMessages.dialogTitle} values={{ orderNumber }} />
        </DashboardModal.Header>
        <Text>
          <FormattedMessage
            {...(variant === "after-refund"
              ? cancelOrderDialogMessages.dialogContentAfterRefund
              : variant === "after-pending-refund"
                ? cancelOrderDialogMessages.dialogContentAfterPendingRefund
                : cancelOrderDialogMessages.dialogContent)}
            values={{
              b: (...chunks) => <b>{chunks}</b>,
            }}
          />
        </Text>

        <Box display="flex" flexDirection="column" gap={3}>
          <Select
            data-test-id="cancel-reason-select"
            name="reasonCode"
            label={intl.formatMessage(cancelOrderDialogMessages.reasonLabel)}
            options={reasonOptions}
            value={reasonCode}
            onChange={({ target }) => setReasonCode(target.value as string)}
          />
          {isOtherReason && (
            <Textarea
              data-test-id="cancel-reason-details"
              name="reason"
              label={intl.formatMessage(cancelOrderDialogMessages.otherReasonLabel)}
              rows={3}
              value={otherReason}
              onChange={event => setOtherReason(event.target.value)}
            />
          )}
          <Text size={2} color="default2">
            <FormattedMessage {...cancelOrderDialogMessages.reasonHint} />
          </Text>
        </Box>

        {errors.length > 0 &&
          errors.map((err, index) => (
            <Text display="block" color="critical1" key={index} data-test-id="dialog-error">
              {getOrderErrorMessage(err, intl)}
            </Text>
          ))}

        <DashboardModal.Actions>
          <BackButton onClick={onClose}>
            <FormattedMessage {...cancelOrderDialogMessages.buttonKeepOrder} />
          </BackButton>
          <ConfirmButton
            data-test-id="submit"
            disabled={!formData}
            onClick={() => formData && onSubmit(formData)}
            transitionState={confirmButtonState}
            type="submit"
          >
            <FormattedMessage {...cancelOrderDialogMessages.buttonCancelOrder} />
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
OrderCancelDialog.displayName = "OrderCancelDialog";
export default OrderCancelDialog;
